import { z } from "zod";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import {
    MonthRecurrenceType,
    QuarterMonth,
} from "@common/types/serviceTemplateRecurrence";

export function refineServiceStartPositioning(
    value: {
        period?: ServiceTemplateRecurrencePeriod;
        weekDay: number | null;
        weekDays?: number[] | null;
        month?: number | null;
        dateOfMonth: number | null;
        datesOfMonth?: number[] | null;
        monthRecurrenceType: MonthRecurrenceType | null;
        quarterMonth?: QuarterMonth | null;
    },
    ctx: z.RefinementCtx,
): void {
    if (value.period === ServiceTemplateRecurrencePeriod.WEEKLY) {
        const weekDays = value.weekDays ?? null;
        if (
            weekDays === null ||
            weekDays.length < 1 ||
            weekDays.some((day) => day < 0 || day > 6)
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["weekDays"],
                message: "Weekly service periods require at least one weekDay",
            });
        }
    }
    if (value.period === ServiceTemplateRecurrencePeriod.MONTHLY) {
        const datesOfMonth = value.datesOfMonth ?? null;
        if (
            datesOfMonth === null ||
            datesOfMonth.length < 1 ||
            datesOfMonth.some((day) => day < 1 || day > 31)
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["datesOfMonth"],
                message:
                    "Monthly service periods require at least one day of month",
            });
        }
    }
    if (
        value.period === ServiceTemplateRecurrencePeriod.QUARTERLY &&
        value.quarterMonth === null
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["quarterMonth"],
            message: "quarterMonth is required for quarterly service periods",
        });
    }
    if (
        value.period === ServiceTemplateRecurrencePeriod.ANNUALLY &&
        value.month === null
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["month"],
            message: "month is required for annual service periods",
        });
    }
    // Only quarterly and annual periods position the service start via
    // monthRecurrenceType/weekDay/dateOfMonth; monthly is driven purely by
    // datesOfMonth. Reject a monthRecurrenceType on any other period so a
    // "first weekday of month" monthly config can't pass validation and then
    // be silently generated on a numeric day-of-month instead.
    const doesPeriodUseMonthPositioning =
        value.period === ServiceTemplateRecurrencePeriod.QUARTERLY ||
        value.period === ServiceTemplateRecurrencePeriod.ANNUALLY;
    if (
        value.period !== undefined &&
        !doesPeriodUseMonthPositioning &&
        value.monthRecurrenceType !== null
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["monthRecurrenceType"],
            message:
                "monthRecurrenceType only applies to quarterly and annual service periods",
        });
    }
    if (
        value.monthRecurrenceType === MonthRecurrenceType.DAY &&
        value.dateOfMonth === null
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["dateOfMonth"],
            message: "dateOfMonth is required when monthRecurrenceType is DAY",
        });
    }
    if (
        value.monthRecurrenceType !== null &&
        value.monthRecurrenceType !== MonthRecurrenceType.DAY &&
        value.weekDay === null
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["weekDay"],
            message:
                "weekDay is required when monthRecurrenceType selects a weekday occurrence",
        });
    }
}

export function refineEndsExclusivity(
    value: {
        endDate?: string | null;
        maxOccurrenceCount?: number | null;
    },
    ctx: z.RefinementCtx,
): void {
    if (value.endDate != null && value.maxOccurrenceCount != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["maxOccurrenceCount"],
            message:
                "Set either an end date or a max occurrence count, not both",
        });
    }
}
